/**
 * LSC - lsc-instant-nav.js (added 2026-10-15)
 * ------------------------------------------------------------------------
 * Purpose: make the desktop top-navigation submenus (simple dropdowns AND
 * mega menus) open and close instantly on hover, with no delay or animation.
 *
 * Why this file exists (instead of editing assets/theme.js):
 *   theme.js defines <details is="details-dropdown"> (DetailsDropdown) and
 *   <details is="details-mega"> (DetailsMega, which extends DetailsDropdown).
 *   When a menu opens, DetailsDropdown.prototype.transition():
 *     - waits 100ms (setTimeout) before marking the panel [open], which is the
 *       attribute the CSS item stagger keys off, and
 *     - calls transitionIn(), which fades the panel in after a 0.2s delay over
 *       0.6s and slides it down from translateY(-105%) over 0.6s
 *       (mega menus: 0.6s slide, plus a 450ms wait before tabs load).
 *   Every one of those timings is written as
 *       theme.config.motionReduced ? 0 : <normal value>
 *   i.e. the theme already has a zero-delay / zero-duration path that it uses
 *   for visitors with "reduce motion" turned on. This file switches the
 *   desktop nav onto that path for everyone, so no theme code is copied or
 *   rewritten and theme updates to these methods keep working.
 *
 * How it works:
 *   We wrap DetailsDropdown.prototype.transition. For menus inside the
 *   desktop header nav (.header__menu) the wrapper sets
 *   theme.config.motionReduced = true, calls the original method, then puts
 *   the flag back. All the code that reads the flag (the setTimeout check,
 *   transitionIn / transitionOut and the mega-menu tab timer) runs
 *   synchronously before the original method's first `await`, so the flag
 *   is only "on" for this menu's open/close and nothing else on the page
 *   ever sees it.
 *   DetailsMega inherits transition() from DetailsDropdown, so the single
 *   patch covers both menu types.
 *
 * Companion file: assets/lsc-instant-nav.css removes the CSS transitions and
 * staggered delays on the menu items themselves.
 *
 * Revert: remove the <script> tag for this file in layout/theme.liquid.
 */
(function () {
  'use strict';

  // Only menus inside the desktop header navigation are made instant.
  // Other details-dropdown elements on the site (e.g. sort-by or
  // country/language pickers) keep the theme's normal animation.
  var NAV_SELECTOR = '.header__menu';

  /**
   * Replace DetailsDropdown.prototype.transition with a wrapper that runs
   * the original method on the theme's zero-delay ("reduced motion") path
   * when the menu is part of the desktop header nav.
   */
  function patchDetailsDropdown() {
    var DetailsDropdown = customElements.get('details-dropdown');

    // Bail out safely if the theme ever renames or removes this element,
    // or if the patch has already been applied (e.g. script loaded twice).
    if (!DetailsDropdown || DetailsDropdown.prototype.__lscInstantNav) return;

    var originalTransition = DetailsDropdown.prototype.transition;
    if (typeof originalTransition !== 'function') return;

    DetailsDropdown.prototype.transition = function (value) {
      var config = window.theme && window.theme.config;

      // Not a desktop-nav menu, or the theme config is missing:
      // behave exactly like the original theme code.
      if (!config || !this.closest(NAV_SELECTOR)) {
        return originalTransition.call(this, value);
      }

      // Remember the visitor's real reduced-motion preference.
      var previousMotionReduced = config.motionReduced;
      config.motionReduced = true;

      try {
        // The original is async. Calling it runs everything up to its first
        // `await` right now, which includes every motionReduced check, and
        // returns a Promise we hand straight back to the caller.
        return originalTransition.call(this, value);
      } finally {
        // Restore immediately so no other component is affected.
        config.motionReduced = previousMotionReduced;
      }
    };

    // Marker so the patch is never applied twice.
    DetailsDropdown.prototype.__lscInstantNav = true;
  }

  // theme.js is loaded (deferred) before this file, but waiting for the
  // element definition makes the patch work regardless of load order.
  // Patching the prototype also covers menu elements that already exist.
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('details-dropdown').then(patchDetailsDropdown);
  }
})();